<script setup lang="ts">
import { useForm } from '@formwerk/core'
import { COUNTRIES, countryCodeFromName, DEFAULT_COUNTRY_NAME } from '#shared/utils/countries'
import { DATE_OF_BIRTH_MIN } from '#shared/utils/dateOfBirth'
import { PrescriptionSchema } from '#shared/utils/prescriptionSchema'

interface PrescriptionFormProps {
  submitAlign?: 'center' | 'start'
  submitTheme?: 'primary' | 'scheme'
}

withDefaults(defineProps<PrescriptionFormProps>(), {
  submitAlign: 'center',
  submitTheme: 'primary',
})

const { formProps, handleSubmit, reset, isSubmitting, values } = useForm({
  schema: PrescriptionSchema,
  initialValues: { country: DEFAULT_COUNTRY_NAME },
  disableHtmlValidation: true,
})

// Mobile numbers without an international prefix are read as local to the selected country
const phoneCountry = computed(() => countryCodeFromName(values.country))

const status = ref<'idle' | 'success' | 'error'>('idle')
const errorDetail = ref<string>()

const today = new Date().toISOString().slice(0, 10)

const onSubmit = handleSubmit(async (data) => {
  status.value = 'idle'
  errorDetail.value = undefined

  try {
    await $fetch('/api/prescription', { method: 'POST', body: data.toObject() })
    status.value = 'success'
    reset()
  }
  catch (error) {
    status.value = 'error'
    errorDetail.value = pabauErrorMessage(error)
  }
})

// formProps ships its own onSubmit that falls back to a native `form.submit()`;
// replace it so only our fetch runs.
const formAttrs = { ...formProps, onSubmit }
</script>

<template>
  <TransitionDefault mode="out-in">
    <p v-if="status === 'success'" class="typo-sans-1 text-center uppercase" role="status">
      {{ $t('form_prescription_success') }}
    </p>

    <form v-else v-bind="formAttrs" class="grid space-y-5">
      <div class="gap-x-5 grid space-y-5 sm:grid-cols-2 sm:space-y-0">
        <TextField :label="$t('first_name')" name="firstName" autocomplete="given-name" required />
        <TextField :label="$t('last_name')" name="lastName" autocomplete="family-name" required />
      </div>
      <DateField :label="$t('date_of_birth')" name="dateOfBirth" autocomplete="bday" :min="DATE_OF_BIRTH_MIN" :max="today" required />

      <TextField :label="$t('street_address')" name="street" autocomplete="street-address" required />
      <div class="gap-x-5 grid space-y-5 sm:grid-cols-2 sm:space-y-0">
        <TextField :label="$t('city')" name="city" autocomplete="address-level2" required />
        <TextField :label="$t('county')" name="county" autocomplete="address-level1" required />
      </div>
      <div class="gap-x-5 grid space-y-5 sm:grid-cols-2 sm:space-y-0">
        <ComboBoxField :label="$t('country')" name="country" required>
          <OptionItem
            v-for="country in COUNTRIES"
            :key="country.code"
            :label="country.name"
            :value="country.name"
          />
        </ComboBoxField>
        <TextField :label="$t('post_code')" name="postCode" autocomplete="postal-code" required />
      </div>

      <TextField :label="$t('email')" name="email" type="email" autocomplete="email" required />
      <PhoneField :label="$t('mobile')" name="mobile" autocomplete="tel" :default-country="phoneCountry" required />
      <TextareaField :label="$t('medication_requested')" name="message" required />
      <TextareaField :label="$t('pharmacy_information')" name="pharmacyInformation" required />
      <CheckboxField :label="$t('marketing_consent')" name="marketingConsent" />

      <!-- Honeypot, hidden from users and assistive tech -->
      <div class="sr-only" aria-hidden="true" inert>
        <TextField label="Company" name="company" autocomplete="off" />
      </div>

      <p v-if="status === 'error'" class="typo-sans-1 c-red text-center uppercase" role="alert">
        {{ errorDetail || $t('form_book_error') }}
      </p>

      <div class="mt-12 flex" :class="submitAlign === 'start' ? 'justify-start' : 'justify-center'">
        <Button
          :title="$t('send')"
          :theme="submitTheme"
          type="submit"
          :disabled="isSubmitting"
          :class="{ 'opacity-50': isSubmitting }"
        />
      </div>
    </form>
  </TransitionDefault>
</template>
